import { format, parseISO } from 'date-fns';
import { toZonedTime, formatInTimeZone, fromZonedTime } from 'date-fns-tz';

/**
 * Default timezone for the system (Pakistan Standard Time)
 */
export const DEFAULT_TIMEZONE = 'Asia/Karachi';

/**
 * Supported timezones with display labels
 */
export const SUPPORTED_TIMEZONES: Record<string, string> = {
  'Asia/Karachi': 'PKT (Pakistan Standard Time)',
  'Asia/Kolkata': 'IST (India Standard Time)',
  'Asia/Dubai': 'GST (Gulf Standard Time)',
  'Asia/Riyadh': 'AST (Arabia Standard Time)',
  'Europe/London': 'GMT/BST (UK Time)',
  'America/New_York': 'EST/EDT (Eastern US)',
  'UTC': 'UTC (Coordinated Universal Time)',
};

export const dateTimeUtils = {
  /**
   * Returns the day of the week (0-6) for a given date string (YYYY-MM-DD),
   * treating the date as a UTC date to avoid local timezone shifts.
   * 0 = Sunday, 1 = Monday, ..., 6 = Saturday
   */
  getDayOfWeek: (dateStr: string): number => {
    // Append T00:00:00Z to ensure it's parsed as UTC midnight
    const date = new Date(`${dateStr}T00:00:00Z`);
    return date.getUTCDay();
  },

  /**
   * Formats a date string or object to YYYY-MM-DD,
   * preserving the "calendar date" regardless of timezone.
   */
  formatDate: (date: Date | string): string => {
    if (typeof date === 'string') {
      // If it looks like ISO, slice it. If it's YYYY-MM-DD, return it.
      if (date.includes('T')) return date.split('T')[0];
      return date;
    }
    // For Date objects, use UTC methods to avoid local shifts
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  },

  /**
   * Get formatted display date (e.g., "01" or "Fri, 01")
   */
  formatDisplayDate: (dateStr: string, options: { showDay?: boolean } = {}): string => {
    const date = new Date(`${dateStr}T00:00:00Z`);
    const day = String(date.getUTCDate()).padStart(2, '0');
    if (options.showDay) {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      return `${days[date.getUTCDay()]} ${day}`;
    }
    return day;
  },

  /**
   * Get days in a specific month (year, month 1-12)
   */
  getDaysInMonth: (year: number, month: number): string[] => {
    const dates: string[] = [];
    const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate(); 
    
    for (let d = 1; d <= daysInMonth; d++) {
      const dayStr = String(d).padStart(2, '0');
      const monthStr = String(month).padStart(2, '0');
      dates.push(`${year}-${monthStr}-${dayStr}`);
    }
    return dates;
  },

  // ================================
  // TIMEZONE-AWARE FUNCTIONS
  // ================================

  /**
   * Get today's date string (YYYY-MM-DD) in a specific timezone
   * This is the core function for determining "today" for attendance
   */
  getTodayInTimezone: (timezone: string = DEFAULT_TIMEZONE): string => {
    const zonedDate = toZonedTime(new Date(), timezone);
    return format(zonedDate, 'yyyy-MM-dd');
  },

  /**
   * Get current time in a specific timezone
   */
  getCurrentTimeInTimezone: (timezone: string = DEFAULT_TIMEZONE): Date => {
    return toZonedTime(new Date(), timezone);
  },

  /**
   * Format a UTC date/time for display in school's timezone
   * Use this for displaying timestamps (created at, marked at, etc.)
   */
  formatDateTimeForDisplay: (
    date: Date | string,
    timezone: string = DEFAULT_TIMEZONE,
    formatStr: string = 'dd MMM yyyy, hh:mm a'
  ): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '-';
    return formatInTimeZone(dateObj, timezone, formatStr);
  },

  /**
   * Format a UTC date for display (date only, no time)
   * Use this for displaying dates like subscription start/end
   */
  formatDateForDisplay: (
    date: Date | string,
    timezone: string = DEFAULT_TIMEZONE,
    formatStr: string = 'dd MMM yyyy'
  ): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '-';
    return formatInTimeZone(dateObj, timezone, formatStr);
  },

  /**
   * Format time only for display
   * Use this for punch times, work start times, etc.
   */
  formatTimeForDisplay: (
    date: Date | string,
    timezone: string = DEFAULT_TIMEZONE,
    formatStr: string = 'hh:mm a'
  ): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '-';
    return formatInTimeZone(dateObj, timezone, formatStr);
  },

  /**
   * Convert a date string (YYYY-MM-DD) from school timezone to UTC for API submission
   * Use when sending date-only data to the backend
   */
  dateStringToUTC: (dateStr: string, timezone: string = DEFAULT_TIMEZONE): string => {
    // For date-only strings, we typically just send them as-is
    // The backend handles timezone context
    return dateStr;
  },

  /**
   * Get the date portion (YYYY-MM-DD) from a UTC timestamp in school's timezone
   * Use when you have a full timestamp but need the calendar date it represents in school time
   */
  getDateFromTimestamp: (date: Date | string, timezone: string = DEFAULT_TIMEZONE): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '';
    const zonedDate = toZonedTime(dateObj, timezone);
    return format(zonedDate, 'yyyy-MM-dd');
  },

  /**
   * Check if a date is today in a specific timezone
   */
  isToday: (dateStr: string, timezone: string = DEFAULT_TIMEZONE): boolean => {
    const today = dateTimeUtils.getTodayInTimezone(timezone);
    return dateStr === today;
  },

  /**
   * Check if a date is in the future in a specific timezone
   */
  isFutureDate: (dateStr: string, timezone: string = DEFAULT_TIMEZONE): boolean => {
    const today = dateTimeUtils.getTodayInTimezone(timezone);
    return dateStr > today;
  },

  /**
   * Get relative time description (e.g., "2 hours ago", "in 3 days")
   * For notifications and activity feeds
   */
  getRelativeTime: (date: Date | string, timezone: string = DEFAULT_TIMEZONE): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return '-';
    
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return dateTimeUtils.formatDateForDisplay(dateObj, timezone);
  },

  /**
   * Format duration in minutes to human readable
   * For work hours display
   */
  formatDuration: (minutes: number): string => {
    if (!minutes || minutes < 0) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  },

  /**
   * A wall-clock time "HH:mm" (24h, as time inputs give it) as "9:05 AM".
   */
  formatClockTime: (time: string): string => {
    const [hours, minutes] = time.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    return `${hours % 12 || 12}:${String(minutes).padStart(2, '0')} ${period}`;
  },

  /**
   * Get timezone display label
   */
  getTimezoneLabel: (timezone: string): string => {
    return SUPPORTED_TIMEZONES[timezone] || timezone;
  },
};

export default dateTimeUtils;
